﻿import { IndexFile } from './types.ts'
import { StorageBackend, StorageBackendConstructor } from './storageBackend.ts'
import { updateInfoForWrite } from './metadataManager.ts'

const BRANCH_SEP = '::'
const SEG_WORKSPACE = 'workspace'
const SEG_INFO_WORKSPACE = 'info-workspace'
const SEG_INFO_GIT = 'info-git'

/**
 * テストや軽量動作検証用のインメモリ実装。
 * `StorageBackend` を実装し、アプリケーション側で差し替えて利用できます。
 */
export const InMemoryStorage: StorageBackendConstructor = class InMemoryStorage implements StorageBackend {
  private rootKey: string
  private currentBranch: string | null = null

  // shared storage across instances keyed by root name
  private static stores: Map<string, {
    index: IndexFile,
    workspaceBlobs: Map<string, string>,
    baseBlobs: Map<string, string>,
    conflictBlobs: Map<string, string>,
    infoBlobs: Map<string, string>
  }> = new Map()

  /**
   * 静的: この実装が利用可能かを同期判定します。
   * テスト/インメモリなので常に true を返します。
   * @returns {boolean} 利用可能なら true
   */
  static canUse(): boolean {
    return true
  }

  /**
   * 利用可能なルート名を返します。
   * @param {string} [namespace] Optional namespace to filter roots
   * @returns {string[]} ルート名の配列
   */
  static availableRoots(namespace?: string): string[] {
    const keys = Array.from(InMemoryStorage.stores.keys())
    // keys are stored as namespace-prefixed keys like 'namespace/root'
    if (namespace) {
      const filtered = keys
        .filter((k) => k.startsWith(namespace + '/'))
        .map((k) => k.slice((namespace + '/').length))
      return filtered.length ? filtered : ['apigit_storage']
    }
    // Legacy behavior: return all registered root names across namespaces
    const roots = keys.map((k) => {
      const parts = k.split('/')
      return parts.length > 1 ? parts.slice(1).join('/') : parts[0]
    })
    const uniq = Array.from(new Set(roots))
    return uniq.length ? uniq : ['apigit_storage']
  }
  
  /**
   * コンストラクタ。互換性のためにディレクトリ名を受け取るが無視する。
   * @param {string} namespace Database namespace
   * @param {string} [directory] Optional directory name (ignored)
   */
  constructor(namespace: string, directory?: string) {
    // Compose a namespace-prefixed store key
    const directoryName = directory ?? `__inmem_${Math.random().toString(36).slice(2)}`
    this.rootKey = namespace ? `${namespace}/${directoryName}` : directoryName
    if (!InMemoryStorage.stores.has(this.rootKey)) {
      InMemoryStorage.stores.set(this.rootKey, {
        index: { head: '', entries: {} },
        workspaceBlobs: new Map(),
        baseBlobs: new Map(),
        conflictBlobs: new Map(),
        infoBlobs: new Map()
      })
    }
  }

  /**
   * Set the current branch for segment-scoped operations.
   * @param {string | null} [branch] Branch name or null
   * @returns {void}
   */
  setBranch(branch?: string | null): void {
    this.currentBranch = branch || null
  }

  /**
   * 初期化処理（インメモリでは何もしない）
   * @returns {Promise<void>} 解決時に初期化完了
   */
  async init(): Promise<void> {
    return
  }

  /**
   * 現在の `IndexFile` を返します。
   * @returns {Promise<IndexFile|null>} IndexFile（常に非null）
   */
  async readIndex(): Promise<IndexFile | null> {
    const store = InMemoryStorage.stores.get(this.rootKey)!
    // Reconstruct entries from infoBlobs map
    const result: IndexFile = { head: store.index.head || '', entries: {} }

    // Determine branch scope and load info entries
    const branch = this.currentBranch || 'main'
    // First, load workspace-local info entries (unprefixed keys)
    this._loadInMemoryWorkspaceInfo(store, result, branch)
    this._loadInMemoryBranchInfo(store, result, branch)
    if ((store.index as any).lastCommitKey) result.lastCommitKey = (store.index as any).lastCommitKey
    // Preserve adapter metadata if present
    if ((store.index as any).adapter) result.adapter = (store.index as any).adapter

    return result
  }

  /**
   * Load workspace-local info entries into result.entries (unprefixed keys)
   * @param {any} store Storage object
   * @param {IndexFile} result Result IndexFile
   * @param {string} branch Branch name
   * @returns {void}
   */
  private _loadInMemoryWorkspaceInfo(store: any, result: IndexFile, branch: string): void {
    for (const [k, v] of store.infoBlobs.entries()) {
      if (k.startsWith(branch + BRANCH_SEP)) continue
      const parsed = this._safeParseInfo(v, k)
      if (parsed) result.entries[k] = parsed
    }
  }

  /**
   * Load branch-scoped info entries into result.entries without overwriting workspace-local entries
   * @param {any} store Storage object
   * @param {IndexFile} result Result IndexFile
   * @param {string} branch Branch name
   * @returns {void}
   */
  private _loadInMemoryBranchInfo(store: any, result: IndexFile, branch: string): void {
    for (const [k, v] of store.infoBlobs.entries()) {
      if (!k.startsWith(branch + BRANCH_SEP)) continue
      const filepath = k.slice((branch + BRANCH_SEP).length)
      if (result.entries[filepath]) continue
      const parsed = this._safeParseInfo(v, k)
      if (!parsed) continue
      if (parsed && parsed.state === 'deleted') continue
      result.entries[filepath] = parsed
    }
  }

  /**
   * Safely parse stored info JSON and log parse errors.
   * @param {string} v JSON text
   * @param {string} key Entry key
   * @returns {any | null} parsed object or null
   */
  private _safeParseInfo(v: string, key: string): any | null {
    try {
      return JSON.parse(v)
    } catch (error) {
      if (typeof console !== 'undefined' && (console as any).debug) (console as any).debug('parse info failed', key, error)
      return null
    }
  }

  /**
   * IndexFile を設定します。
   * @param {IndexFile} index 書き込む IndexFile
   * @returns {Promise<void>}
   */
  async writeIndex(index: IndexFile): Promise<void> {
    const store = InMemoryStorage.stores.get(this.rootKey)!
    // write entries individually to infoBlobs, then persist meta
    const entries = index.entries || {}

    // Persist index entries into workspace-local info (unprefixed keys)
    // Only write info entries for files that exist in workspace or in base (branch-scoped)
    const branch = this.currentBranch || 'main'
    for (const filepath of Object.keys(entries)) {
      const existsInWorkspace = store.workspaceBlobs.has(filepath)
      const existsInBase = store.baseBlobs.has(`${branch}${BRANCH_SEP}${filepath}`)
      if (!existsInWorkspace && !existsInBase) continue
      store.infoBlobs.set(filepath, JSON.stringify(entries[filepath]))
    }
    const meta: any = { head: index.head }
    if ((index as any).lastCommitKey) meta.lastCommitKey = (index as any).lastCommitKey
    if ((index as any).adapter) meta.adapter = (index as any).adapter
    store.index = Object.assign({}, meta, { entries: {} })
  }

  /**
   * 指定パスに対して文字列コンテンツを保存します。
   * @param {string} filepath ファイルパス
   * @param {string} content ファイル内容
   * @param {any} [segment] Storage segment
   * @returns {Promise<void>}
   */
  async writeBlob(filepath: string, content: string, segment?: any): Promise<void> {
    const seg = segment || SEG_WORKSPACE
    const store = InMemoryStorage.stores.get(this.rootKey)!
    this._applyBlobToStore(store, seg, filepath, content)
    // update info metadata when writing to workspace/base/conflict
    // For workspace writes, if a git base exists for this file, treat this
    // update as coming from the git base and build workspace/info based on
    // the git-scoped info; otherwise fall back to the default helper.
    if (seg === SEG_WORKSPACE) {
      const handled = await this._handleWorkspaceBlobWrite(store, filepath, content)
      if (handled) return
    }
    // For explicit info-workspace/info-git writes, we've already stored
    // the provided content directly; skip the generic metadata updater
    if (seg === SEG_INFO_WORKSPACE || seg === SEG_INFO_GIT) return

    // conflictBlob is a content-only blob; don't update metadata as it would
    // overwrite the conflict entry with minimal data and lose remoteSha/state fields
    if (seg === 'conflictBlob') return

    // For conflict segment, always use direct (unprefixed) info update to ensure
    // workspace-local entries are maintained and existing fields like remoteSha
    // are preserved in the conflict entry.
    const wrapped = (seg === SEG_WORKSPACE || seg === 'conflict')
      ? this._wrapStoreForInfoNoPrefix(store)
      : this._wrapStoreForInfoPrefix(store)
    await updateInfoForWrite(wrapped, filepath, seg, content)
  }

  /**
   * Handle workspace blob writes that should be based on existing git-scoped info.
   * Returns true when the operation is handled and caller should return early.
   * @param {any} store Storage object
   * @param {string} filepath File path
   * @param {string} content File content
   * @returns {Promise<boolean>}
   */
  private async _handleWorkspaceBlobWrite(store: any, filepath: string, content: string): Promise<boolean> {
    const branch = this.currentBranch || 'main'
    const gitBaseKey = `${branch}${BRANCH_SEP}${filepath}`
    if (!store.baseBlobs.has(gitBaseKey)) return false
    const gitInfoTxt = store.infoBlobs.has(gitBaseKey) ? store.infoBlobs.get(gitBaseKey)! : null
    let existing: any = undefined
    if (gitInfoTxt) {
      try { existing = JSON.parse(gitInfoTxt) } catch (error) { if (typeof console !== 'undefined' && (console as any).debug) (console as any).debug('parse gitInfoTxt failed', error); existing = undefined }
    }
    const sha = await this.shaOf(content)
    const now = Date.now()
    const entry = this._buildWorkspaceInfoEntry(existing, filepath, sha, now)
    store.infoBlobs.set(filepath, JSON.stringify(entry))
    return true
  }

  /**
   * Persist a blob into the appropriate in-memory map for the segment.
   * @param {any} store Storage object
   * @param {string} seg Segment name
   * @param {string} filepath File path
   * @param {string} content File content
   * @returns {void}
   */
  private _applyBlobToStore(store: any, seg: string, filepath: string, content: string): void {
    const branch = this.currentBranch || 'main'
    if (seg === SEG_WORKSPACE) store.workspaceBlobs.set(filepath, content)
    else if (seg === 'base') store.baseBlobs.set(`${branch}${BRANCH_SEP}${filepath}`, content)
    else if (seg === 'conflict') store.conflictBlobs.set(`${branch}${BRANCH_SEP}${filepath}`, content)
    // v0.0.4: conflictBlob segment for on-demand fetched conflict content
    else if (seg === 'conflictBlob') store.conflictBlobs.set(`${branch}${BRANCH_SEP}conflictBlob${BRANCH_SEP}${filepath}`, content)
    // Writes to the generic 'info' segment should create/update the
    // workspace-local (unprefixed) info entry so that subsequent
    // readBlob('info') returns the expected value. Dedicated helpers
    // exist for explicit workspace/git info variants.
    else if (seg === 'info') store.infoBlobs.set(filepath, content)
    else if (seg === SEG_INFO_WORKSPACE) store.infoBlobs.set(filepath, content)
    else if (seg === SEG_INFO_GIT) store.infoBlobs.set(`${branch}${BRANCH_SEP}${filepath}`, content)
    else throw new Error('unknown segment')
  }

  /**
   * Build the info entry for a write into a given segment.
   * @param {string} seg Segment name
   * @param {any} existing Existing info entry
   * @param {string} filepath File path
   * @param {string} sha SHA hash
   * @param {number} now Current timestamp
   * @returns {any} info entry object
   */
  private _buildInfoEntryForSeg(seg: string, existing: any, filepath: string, sha: string, now: number): any {
    if (seg === SEG_WORKSPACE) return this._buildWorkspaceInfoEntry(existing, filepath, sha, now)
    if (seg === 'base') return this._buildBaseInfoEntry(existing, filepath, sha, now)
    if (seg === 'conflict') return this._buildConflictInfoEntry(existing, filepath, now)
    // v0.0.4: conflictBlob is same as conflict (don't update info)
    if (seg === 'conflictBlob') return this._buildConflictInfoEntry(existing, filepath, now)
    return { path: filepath, updatedAt: now }
  }

  /**
   * Build info entry when writing to the workspace segment
   * @param {any} existing Existing info entry
   * @param {string} filepath File path
   * @param {string} sha SHA hash
   * @param {number} now Current timestamp
   * @returns {any}
   */
  private _buildWorkspaceInfoEntry(existing: any, filepath: string, sha: string, now: number): any {
    const entry: any = { path: filepath, updatedAt: now }
    if (existing && existing.baseSha) entry.baseSha = existing.baseSha
    entry.workspaceSha = sha
    entry.state = entry.baseSha ? 'modified' : 'added'
    if (existing && existing.remoteSha) entry.remoteSha = existing.remoteSha
    return entry
  }

  /**
   * Build info entry when writing to the base segment
   * @param {any} existing Existing info entry
   * @param {string} filepath File path
   * @param {string} sha SHA hash
   * @param {number} now Current timestamp
   * @returns {any}
   */
  private _buildBaseInfoEntry(existing: any, filepath: string, sha: string, now: number): any {
    const entry: any = { path: filepath, updatedAt: now }
    if (existing && existing.workspaceSha) entry.workspaceSha = existing.workspaceSha
    entry.baseSha = sha
    entry.state = 'base'
    if (existing && existing.remoteSha) entry.remoteSha = existing.remoteSha
    return entry
  }

  /**
   * Build info entry when writing to the conflict segment
   * @param {any} existing Existing info entry
   * @param {string} filepath File path
   * @param {number} now Current timestamp
   * @returns {any}
   */
  private _buildConflictInfoEntry(existing: any, filepath: string, now: number): any {
    const entry: any = { path: filepath, updatedAt: now }
    if (existing && existing.baseSha) entry.baseSha = existing.baseSha
    if (existing && existing.workspaceSha) entry.workspaceSha = existing.workspaceSha
    if (existing && existing.remoteSha) entry.remoteSha = existing.remoteSha
    entry.state = 'conflict'
    return entry
  }

  /**
   * 指定パスの内容を取得します。
   * @param {string} filepath ファイルパス
   * @param {any} [segment] Storage segment
   * @returns {Promise<string|null>} 内容、存在しなければ null
   */
  async readBlob(filepath: string, segment?: any): Promise<string | null> {
    const store = InMemoryStorage.stores.get(this.rootKey)!
    return this._readInMemoryBlob(store, filepath, segment)
  }

  /**
   * Read blob with segment logic extracted for clarity.
   * @param {any} store Storage object
   * @param {string} filepath File path
   * @param {any} [segment] Storage segment
   * @returns {string | null}
   */
  private _readInMemoryBlob(store: any, filepath: string, segment?: any): string | null {
    // Delegate segment-specific handling to a helper for clarity
    if (segment !== undefined) return this._readInMemoryBlobWithSegment(store, filepath, String(segment))

    const workspace = store.workspaceBlobs
    if (workspace && workspace.has(filepath)) return workspace.get(filepath)!
    const base = store.baseBlobs
    const branch = this.currentBranch || 'main'
    if (base && base.has(`${branch}${BRANCH_SEP}${filepath}`)) return base.get(`${branch}${BRANCH_SEP}${filepath}`)!
    return null
  }

  /**
   * Handle reading when a segment is explicitly provided.
   * @param {any} store Storage object
   * @param {string} filepath File path
   * @param {string} seg Segment name
   * @returns {string | null}
   */
  private _readInMemoryBlobWithSegment(store: any, filepath: string, seg: string): string | null {
    const branch = this.currentBranch || 'main'
    // Delegate specific segment handling to helpers to reduce cognitive complexity
    if (seg === SEG_INFO_WORKSPACE) return this._handleInfoWorkspaceSegment(store, filepath)
    if (seg === SEG_INFO_GIT) return this._handleInfoGitSegment(store, branch, filepath)
    if (seg === 'info') return this._handleInfoGenericSegment(store, branch, filepath)
    if (seg === 'conflictBlob') return this._handleConflictBlobSegment(store, branch, filepath)

    const segmentToStore = {
      [SEG_WORKSPACE]: store.workspaceBlobs,
      base: store.baseBlobs,
      conflict: store.conflictBlobs,
      info: store.infoBlobs,
    } as Record<string, Map<string, string>>
    return this._getFromSegmentMap(segmentToStore, seg, branch, filepath)
  }

  /**
   * Handle explicit workspace-info segment read.
   * @param {any} store Storage object
   * @param {string} filepath File path
   * @returns {string|null}
   */
  private _handleInfoWorkspaceSegment(store: any, filepath: string): string | null {
    const infoWs = this._readInfoWorkspace(store, filepath)
    return infoWs !== undefined ? infoWs : null
  }

  /**
   * Handle explicit git-scoped info segment read.
   * @param {any} store Storage object
   * @param {string} branch Branch name
   * @param {string} filepath File path
   * @returns {string|null}
   */
  private _handleInfoGitSegment(store: any, branch: string, filepath: string): string | null {
    const infoGit = this._readInfoGit(store, branch, filepath)
    return infoGit !== undefined ? infoGit : null
  }

  /**
   * Handle generic info segment read (prefer workspace then git-prefixed).
   * @param {any} store Storage object
   * @param {string} branch Branch name
   * @param {string} filepath File path
   * @returns {string|null}
   */
  private _handleInfoGenericSegment(store: any, branch: string, filepath: string): string | null {
    const infoWs = this._readInfoWorkspace(store, filepath)
    if (infoWs !== undefined) return infoWs
    const infoGit = this._readInfoGit(store, branch, filepath)
    return infoGit !== undefined ? infoGit : null
  }

  /**
   * Handle conflictBlob explicit segment read.
   * @param {any} store Storage object
   * @param {string} branch Branch name
   * @param {string} filepath File path
   * @returns {string|null}
   */
  private _handleConflictBlobSegment(store: any, branch: string, filepath: string): string | null {
    const conflictBlob = this._readConflictBlob(store, branch, filepath)
    return conflictBlob !== undefined ? conflictBlob : null
  }

  /**
   * Read a branch-prefixed info entry if present.
   * @param {any} store storage object
   * @param {string} branch branch name
   * @param {string} filepath file path
   * @returns {string|null|undefined} stored info string or undefined when not present
   */
  private _readInfoGit(store: any, branch: string, filepath: string): string | null | undefined {
    if (store.infoBlobs.has(`${branch}${BRANCH_SEP}${filepath}`)) return store.infoBlobs.get(`${branch}${BRANCH_SEP}${filepath}`)!
    return undefined
  }

  /**
   * Read a workspace-local info entry if present.
   * @param {any} store storage object
   * @param {string} filepath file path
   * @returns {string|null|undefined} stored info string or undefined when not present
   */
  private _readInfoWorkspace(store: any, filepath: string): string | null | undefined {
    if (store.infoBlobs.has(filepath)) return store.infoBlobs.get(filepath)!
    return undefined
  }

  /**
   * Read an on-demand conflict blob if present.
   * @param {any} store storage object
   * @param {string} branch branch name
   * @param {string} filepath file path
   * @returns {string|null|undefined} stored conflict blob or undefined when not present
   */
  private _readConflictBlob(store: any, branch: string, filepath: string): string | null | undefined {
    const key = `${branch}${BRANCH_SEP}conflictBlob${BRANCH_SEP}${filepath}`
    if (store.conflictBlobs.has(key)) return store.conflictBlobs.get(key)!
    return undefined
  }

  /**
   * Helper to read from a mapped segment store using prefixed/unprefixed keys.
   * @param {Record<string, Map<string, string>>} map segment->store map
   * @param {string} seg requested segment
   * @param {string} branch branch name for prefixed keys
   * @param {string} filepath file path to lookup
   * @returns {string|null} stored value or null
   */
  private _getFromSegmentMap(map: Record<string, Map<string, string>>, seg: string, branch: string, filepath: string): string | null {
    const m = map[seg]
    if (!m) return null
    if (m.has(filepath)) return m.get(filepath)!
    const key = `${branch}${BRANCH_SEP}${filepath}`
    return m.has(key) ? m.get(key)! : null
  }

  /**
   * 指定パスのエントリを削除します。
   * @param {string} filepath ファイルパス
   * @param {any} [segment] Storage segment
   * @returns {Promise<void>}
   */
  async deleteBlob(filepath: string, segment?: any): Promise<void> {
    // If segment specified, delete only that segment
    const store = InMemoryStorage.stores.get(this.rootKey)!
    const branch = this.currentBranch || 'main'
    if (segment === SEG_WORKSPACE) { store.workspaceBlobs.delete(filepath); return }
    if (segment === 'base') { store.baseBlobs.delete(`${branch}${BRANCH_SEP}${filepath}`); return }
    if (segment === 'conflict') { store.conflictBlobs.delete(`${branch}${BRANCH_SEP}${filepath}`); return }
    // v0.0.4: conflictBlob segment deletion
    if (segment === 'conflictBlob') { store.conflictBlobs.delete(`${branch}${BRANCH_SEP}conflictBlob${BRANCH_SEP}${filepath}`); return }
    if (segment === 'info') { store.infoBlobs.delete(filepath); store.infoBlobs.delete(`${branch}${BRANCH_SEP}${filepath}`); return }
    if (segment === SEG_INFO_WORKSPACE) { store.infoBlobs.delete(filepath); return }
    if (segment === SEG_INFO_GIT) { store.infoBlobs.delete(`${branch}${BRANCH_SEP}${filepath}`); return }
    // otherwise delete from all segments
    store.workspaceBlobs.delete(filepath)
    store.baseBlobs.delete(`${branch}${BRANCH_SEP}${filepath}`)
    store.conflictBlobs.delete(`${branch}${BRANCH_SEP}${filepath}`)
    store.conflictBlobs.delete(`${branch}${BRANCH_SEP}conflictBlob${BRANCH_SEP}${filepath}`)
    store.infoBlobs.delete(`${branch}${BRANCH_SEP}${filepath}`)
    store.infoBlobs.delete(filepath)
  }

  /**
   * 指定プレフィックス配下のファイル一覧を取得します。
   * @param {string} [prefix] プレフィックス（例: 'dir/sub'）
   * @param {any} [segment] セグメント（'workspace' 等）。省略時は 'workspace'
   * @param {boolean} [recursive] サブディレクトリも含めるか。省略時は true
   * @returns {Promise<Array<{ path: string; info: string | null }>>}
   */
  async listFiles(prefix?: string, segment?: any, recursive = true): Promise<Array<{ path: string; info: string | null }>> {
    const seg = segment || SEG_WORKSPACE
    const store = InMemoryStorage.stores.get(this.rootKey)!
    const segmentToStore = {
      [SEG_WORKSPACE]: store.workspaceBlobs,
      base: store.baseBlobs,
      conflict: store.conflictBlobs,
      info: store.infoBlobs,
    } as Record<string, Map<string, string>>

    const m = segmentToStore[String(seg)]
    if (!m) return []

    const p = prefix ? prefix.replace(/^\/+|\/+$/g, '') : ''
    const allKeys = Array.from(m.keys())
    const keys = this._resolveKeysForList(allKeys, String(seg), p, recursive)

    return this._collectFilesInMemory(keys, store)
  }

  /**
   * Resolve and filter keys for `listFiles` into final key list.
   * @param {string[]} allKeys All keys from store
   * @param {string} seg Segment name
   * @param {string} p Path prefix
   * @param {boolean} recursive Whether to include subdirectories
   * @returns {string[]} filtered keys
   */
  private _resolveKeysForList(allKeys: string[], seg: string, p: string, recursive: boolean): string[] {
    let keys = allKeys.slice()
    if (seg === 'info') {
      const branch = this.currentBranch || 'main'
      const unpref = keys.filter((k) => !k.startsWith(branch + BRANCH_SEP))
      const pref = keys.filter((k) => k.startsWith(branch + BRANCH_SEP)).map((k) => k.slice((branch + BRANCH_SEP).length))
      keys = Array.from(new Set(unpref.concat(pref)))
    } else if (seg !== SEG_WORKSPACE) {
      const branch = this.currentBranch || 'main'
      keys = keys.filter((k) => k.startsWith(branch + BRANCH_SEP)).map((k) => k.slice((branch + BRANCH_SEP).length))
    }
    return this._filterKeys(keys, p, recursive)
  }

  /**
   * Collect file info objects for keys array (InMemory implementation).
   * @param {string[]} keys Array of keys
   * @param {any} store Storage object
   * @returns {Array<{path:string, info:string|null}>}
   */
  private _collectFilesInMemory(keys: string[], store: any): Array<{ path: string; info: string | null }> {
    const out: Array<{ path: string; info: string | null }> = []
    const branch = this.currentBranch || 'main'
    for (const k of keys) {
      let info: string | null = null
      if (store.infoBlobs.has(k)) info = store.infoBlobs.get(k)!
      else if (store.infoBlobs.has(`${branch}${BRANCH_SEP}${k}`)) info = store.infoBlobs.get(`${branch}${BRANCH_SEP}${k}`)!
      out.push({ path: k, info })
    }
    return out
  }

  /**
   * Filter keys by prefix and recursion flag for InMemoryStorage
   * @param {string[]} keys Array of keys
   * @param {string} p Path prefix
   * @param {boolean} recursive Whether to include subdirectories
   * @returns {string[]}
   */
  private _filterKeys(keys: string[], p: string, recursive: boolean): string[] {
    if (p) keys = keys.filter((k) => k === p || k.startsWith(p + '/'))
    if (!recursive) {
      keys = keys.filter((k) => {
        const rest = p ? k.slice(p.length + (p ? 1 : 0)) : k
        return !rest.includes('/')
      })
    }
    return keys
  }

  /**
   * Wrap store to present branch-prefixed info accessors
   * @param {any} store Storage object
   * @returns {any} wrapped store view for info access
   */
  private _wrapStoreForInfoPrefix(store: any): any {
    const branch = this.currentBranch || 'main'
    /**
     * Create branch-prefixed storage key
     * @param {string} k key path
     * @returns {string} prefixed key
     */
    const prefixKey = (k: string) => `${branch}${BRANCH_SEP}${k}`
    return Object.assign({}, store, {
      infoBlobs: {
        /**
         * Check whether prefixed key exists
         * @param {string} k key
         * @returns {boolean}
         */
        has: (k: string) => store.infoBlobs.has(k) || store.infoBlobs.has(prefixKey(k)),
        /**
         * Get value preferring unprefixed then branch-prefixed key
         * @param {string} k key
         * @returns {string | undefined}
         */
        get: (k: string) => {
          if (store.infoBlobs.has(k)) return store.infoBlobs.get(k)
          return store.infoBlobs.get(prefixKey(k))
        },
        /**
         * Set value: prefer updating an existing unprefixed entry if present;
         * otherwise write into the branch-prefixed key. This preserves the
         * expected read semantics where workspace-local info takes precedence.
         * @param {string} k key
         * @param {string} v value
         * @returns {any}
         */
        set: (k: string, v: string) => {
          if (store.infoBlobs.has(k)) return store.infoBlobs.set(k, v)
          return store.infoBlobs.set(prefixKey(k), v)
        },
        /**
         * Delete both unprefixed and prefixed keys
         * @param {string} k key
         * @returns {boolean}
         */
        delete: (k: string) => { store.infoBlobs.delete(k); return store.infoBlobs.delete(prefixKey(k)) }
      }
    })
  }

  /**
   * Wrap store to present unprefixed info accessors
   * @param {any} store Storage object
   * @returns {any} wrapped store view for info access
   */
  private _wrapStoreForInfoNoPrefix(store: any): any {
    return Object.assign({}, store, {
      infoBlobs: {
        /**
         * Check whether key exists
         * @param {string} k key
         * @returns {boolean}
         */
        has: (k: string) => store.infoBlobs.has(k),
        /**
         * Get value for key
         * @param {string} k key
         * @returns {string | undefined}
         */
        get: (k: string) => store.infoBlobs.get(k),
        /**
         * Set value for key
         * @param {string} k key
         * @param {string} v value
         * @returns {void}
         */
        set: (k: string, v: string) => store.infoBlobs.set(k, v),
        /**
         * Delete key
         * @param {string} k key
         * @returns {boolean}
         */
        delete: (k: string) => store.infoBlobs.delete(k)
      }
    })
  }

  /**
   * Calculate SHA-1 hex digest of given content.
   * @param {string} content Input string
   * @returns {Promise<string>} Hex encoded SHA-1 digest
   */
  private async shaOf(content: string): Promise<string> {
    const encoder = new TextEncoder()
    const data = encoder.encode(content)
    const hashBuffer = await crypto.subtle.digest('SHA-1', data)
    const hashArray = Array.from(new Uint8Array(hashBuffer))
    return hashArray.map((b) => b.toString(16).padStart(2, '0')).join('')
  }

  /**
   * 指定されたルート名を削除します
   * @param {string} rootName 削除するルート名
   * @returns {void}
   */
  static delete(rootName: string): void {
    // Accept either a full namespaced key or a bare directory name.
    if (InMemoryStorage.stores.has(rootName)) {
      InMemoryStorage.stores.delete(rootName)
      return
    }
    // If a bare rootName was provided, try to find a namespaced key that ends with it
    for (const key of Array.from(InMemoryStorage.stores.keys())) {
      if (key === rootName || key.endsWith('/' + rootName)) {
        InMemoryStorage.stores.delete(key)
        return
      }
    }
    throw new Error(`InMemory root "${rootName}" not found`)
  }

}

export default InMemoryStorage

